import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { BaseComponent } from '@shared/classes';
import {
  FrequencyChallengeComponent,
  StepChallengeComponent,
} from '@shared/components/challenges';
import { RewardComponent } from '@shared/components/rewards/reward.component';
import { OfferType, SliderItemType } from '@shared/constants';
import { SwipeDirective } from '@shared/directives';
import { Reward } from '@shared/factory';
import { AnyChallenge, SwipePayload } from '@shared/models';

@Component({
  selector: 'b2c-free-mode',
  styleUrl: './free-mode.component.scss',
  templateUrl: './free-mode.component.html',
  host: {
    '(window:resize)': 'onResize()',
  },
  imports: [
    SwipeDirective,
    RewardComponent,
    StepChallengeComponent,
    FrequencyChallengeComponent,
  ],
})
export class FreeModeSliderComponent
  extends BaseComponent
  implements AfterViewInit
{
  itemType = input.required<SliderItemType>();
  items = input.required<(AnyChallenge | Reward)[]>();

  readonly OfferType = OfferType;
  readonly SliderItemType = SliderItemType;

  sliderRef = viewChild.required<ElementRef>('slider');
  containerRef = viewChild.required<ElementRef>('sliderContainer');

  rewards = computed(() => this.items() as Reward[]);
  challenges = computed(() => this.items() as AnyChallenge[]);

  public maxIndex = signal(0);
  public currentIndex = signal(0);
  public indexChange = output<{
    index: number;
    maxIndex: number;
    visibleSlides: number;
    visibleSlidesRaw: number;
  }>();

  private visibleSlides = 1;
  private slideWidth = 0;
  private currentTranslateX = 0;
  private dragStartX = 0;
  private isDragging = false;
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    super();
    effect(() => {
      if (this.dataService.reloadSlider()) {
        this.initSlider();
      }
    });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Give DOM time to render - use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        setTimeout(() => this.initSlider(), 150);
      });
    }
  }

  onResize(): void {
    this.initSlider();
  }

  next(): void {
    if (this.currentIndex() < this.maxIndex()) {
      this.currentIndex.update((i) => i + 1);
      this.animateToCurrentIndex();
      this.emitIndexChange();
    }
  }

  prev(): void {
    if (this.currentIndex() > 0) {
      this.currentIndex.update((i) => i - 1);
      this.animateToCurrentIndex();
      this.emitIndexChange();
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft') this.prev();
    if (event.key === 'ArrowRight') this.next();
  }

  panStart(): void {
    if (this.slideWidth <= 0 || this.maxIndex() === 0) return;
    this.isDragging = true;
    this.dragStartX = this.currentTranslateX;
    this.setTransition(false);
  }

  panMove(event: SwipePayload): void {
    if (!this.isDragging) return;

    let newX = this.dragStartX + event.deltaX;
    const minX = -this.maxIndex() * this.slideWidth;

    // Apply resistance at boundaries
    if (newX > 0) {
      newX = newX * 0.3;
    } else if (newX < minX) {
      newX = minX + (newX - minX) * 0.3;
    }

    this.setTransform(newX);
  }

  panEnd(event: SwipePayload): void {
    if (!this.isDragging) return;
    this.isDragging = false;

    if (this.slideWidth <= 0) {
      this.animateToCurrentIndex();
      return;
    }

    const threshold = this.slideWidth * 0.25;
    const velocity = event.velocityX;

    let targetIndex = this.currentIndex();

    // Swipe left (next) - deltaX is negative
    if (event.deltaX < -threshold || velocity < -0.3) {
      targetIndex = Math.min(this.currentIndex() + 1, this.maxIndex());
    }
    // Swipe right (prev) - deltaX is positive
    else if (event.deltaX > threshold || velocity > 0.3) {
      targetIndex = Math.max(this.currentIndex() - 1, 0);
    }

    this.currentIndex.set(targetIndex);
    this.animateToCurrentIndex();
    this.emitIndexChange();
  }

  private initSlider(): void {
    const slider = this.sliderRef()?.nativeElement;
    const container = this.containerRef()?.nativeElement;
    if (!slider || !container) return;

    // Reset transform first to get accurate measurements
    slider.style.transition = 'none';
    slider.style.transform = 'translate3d(0, 0, 0)';

    const domItems = slider.querySelectorAll('.item');
    // Use DOM count for maxIndex (actual rendered items)
    const domItemCount = domItems.length;

    if (domItemCount === 0) {
      this.maxIndex.set(0);
      return;
    }

    // Force reflow to ensure transform is applied before measuring
    void slider.offsetHeight;

    // Calculate slide width from actual rendered items
    if (domItems.length >= 2) {
      const first = domItems[0].getBoundingClientRect();
      const second = domItems[1].getBoundingClientRect();
      this.slideWidth = second.left - first.left;
    } else if (domItems.length === 1) {
      const item = domItems[0];
      const rect = item.getBoundingClientRect();
      const styles = getComputedStyle(item);
      const ml = parseFloat(styles.marginLeft) || 0;
      const mr = parseFloat(styles.marginRight) || 0;
      this.slideWidth = rect.width + ml + mr;
    }

    // Fallback: if slideWidth is still invalid, calculate from first item
    if (this.slideWidth <= 0 && domItems.length > 0) {
      const item = domItems[0];
      const rect = item.getBoundingClientRect();
      const styles = getComputedStyle(item);
      const ml = parseFloat(styles.marginLeft) || 0;
      const mr = parseFloat(styles.marginRight) || 0;
      this.slideWidth = rect.width + ml + mr;
    }

    if (this.slideWidth <= 0) {
      this.maxIndex.set(0);
      return;
    }

    // Calculate how many slides are visible in the container
    const containerWidth = container.getBoundingClientRect().width;
    const calculatedVisibleSlides =
      Math.floor(containerWidth / this.slideWidth) || 1;
    this.visibleSlides = Math.min(calculatedVisibleSlides, domItemCount);

    // maxIndex = total items - visible slides (number of scroll positions)
    // If all items fit in view, maxIndex = 0 (no scrolling needed)
    const max = Math.max(0, domItemCount - this.visibleSlides);

    this.maxIndex.set(max);

    // Reset to first item on init
    this.currentIndex.set(0);
    this.currentTranslateX = 0;
    this.setTransition(false);
    this.setTransform(0);

    // Emit initial state
    this.emitIndexChange();
  }

  private animateToCurrentIndex(): void {
    this.currentTranslateX = -this.currentIndex() * this.slideWidth;
    this.setTransition(true);
    this.setTransform(this.currentTranslateX);
  }

  private setTransition(animate: boolean): void {
    const slider = this.sliderRef()?.nativeElement;
    if (slider) {
      slider.style.transition = animate
        ? 'transform 0.35s cubic-bezier(0.25, 0.1, 0.25, 1)'
        : 'none';
    }
  }

  private setTransform(x: number): void {
    const slider = this.sliderRef()?.nativeElement;
    if (slider) {
      slider.style.transform = `translate3d(${x}px, 0, 0)`;
    }
  }

  private emitIndexChange(): void {
    this.indexChange.emit({
      index: this.currentIndex(),
      maxIndex: this.maxIndex(),
      visibleSlides: this.visibleSlides,
      visibleSlidesRaw: this.visibleSlides,
    });
  }
}
